import { GoogleAuthenticator, MMButton } from '@components'
import cn from '@integrations/classnames'
import { globalSettings, LoginStatuses, TwoFactorTypes, useAuth, useOtpQry } from 'mmfintech-backend-api'
import { formatPhoneNumber, isValidFunction, isValidString, tr, useValidUntilTimer } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import styles from './Otp.module.scss'

interface OtpProps {
  onCancel?: () => void
}

const Otp = ({ onCancel }: OtpProps) => {
  const {
    code,
    codeSize,
    attempts,
    challenge,
    handleCodeChanged,
    handleCancel,
    handleSubmit,
    handleResend,
    verifyOtpError,
    verifyOtpFetching,
    resendOtpFetching,
    resendTimer
  } = useOtpQry()

  const userStatus = useAuth()
  const timer = useValidUntilTimer()

  const { sentTo, twoFactorType, totpSecretKeyUri } = challenge || {}

  const isTotp = twoFactorType === TwoFactorTypes.TOTP
  const isTotpSetup = isTotp && isValidString(totpSecretKeyUri)

  function internalCancel() {
    isValidFunction(onCancel) && onCancel()
    handleCancel()
  }

  function prepareTitle() {
    if (isTotp) {
      if (userStatus === LoginStatuses.LOGGED_IN) {
        return tr('FRONTEND.VERIFY_OTP.TITLE_VERIFY_TOTP', 'Authenticator verification')
      }

      return tr('FRONTEND.VERIFY_OTP.TITLE_TOTP', 'Authenticator confirmation')
    }

    if (twoFactorType === TwoFactorTypes.SMS) {
      if (userStatus === LoginStatuses.LOGGED_IN) {
        return tr('FRONTEND.VERIFY_OTP.TITLE_VERIFY_SMS', 'SMS verification')
      }

      return tr('FRONTEND.VERIFY_OTP.TITLE_SMS', 'SMS confirmation')
    }

    if (userStatus === LoginStatuses.SECOND_FACTOR || userStatus === LoginStatuses.LOGGED_IN) {
      return tr('FRONTEND.VERIFY_OTP.TITLE_VERIFY_EMAIL', 'Email verification')
    }
    return tr('FRONTEND.VERIFY_OTP.TITLE_EMAIL', 'Email confirmation')
  }

  return (
    <div
      className={cn(
        styles['otp-wrapper'],
        'otp-wrapper',
        'layout-modal-container',
        'font-body-regular',
        'modal-frame-surface',
        'raised-frame-surface',
        'shadow-card'
      )}
      data-test='otp-verification-dialog'
    >
      <div className={cn(styles['otp-title'], 'otp-title', 'font-heading-regular')}>{prepareTitle()}</div>

      {isTotpSetup ? (
        <div className={cn(styles['otp-totp-setup'], 'otp-totp-setup')}>
          <GoogleAuthenticator totpSecretKeyUri={totpSecretKeyUri} />
        </div>
      ) : (
        <div className={cn(styles['otp-subtitle'], 'otp-subtitle')}>
          {isTotp
            ? tr(
                'FRONTEND.VERIFY_OTP.TOTP_TARGET_LABEL',
                'Please, enter the verification code from your authenticator app.'
              )
            : tr('FRONTEND.VERIFY_OTP.TARGET_LABEL', "Please, enter the verification code that we've sent to:")}
          {isTotp ? null : <span>{twoFactorType === TwoFactorTypes.SMS ? formatPhoneNumber(sentTo) : sentTo}</span>}
        </div>
      )}

      <input
        id='otp'
        type='tel'
        className={cn(styles['otp-input'], 'otp-input', 'font-heading-regular', {
          [styles['otp-wrong']]: code?.length === codeSize && verifyOtpError != null,
          'otp-wrong': code?.length === codeSize && verifyOtpError != null
        })}
        autoComplete='off'
        onChange={(e) => handleCodeChanged(e.target.value)}
        value={code}
        maxLength={codeSize}
        autoFocus
        placeholder={tr('FRONTEND.VERIFY_OTP.ENTER_CODE', 'Enter code')}
        disabled={timer.expired || attempts >= globalSettings.otpMaxAttempts}
        data-test='tel-input'
        // No Enter handler here: the modal (`ModalDialog`) already clicks its primary button on Enter, and only while it is
        // enabled. A second handler sent the code twice (keydown via the modal, keyup here) and could submit a short code.
      />

      <ErrorDisplay error={verifyOtpError} />

      <div className={cn(styles['buttons-container'], 'buttons-container')}>
        <MMButton
          type='submit'
          variant='primary'
          caption={tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
          loading={resendOtpFetching || verifyOtpFetching}
          onClick={handleSubmit}
          disabled={code.length !== codeSize || attempts >= globalSettings.otpMaxAttempts || timer.expired}
          fullWidth
          data-test='button-submit'
        />

        {onCancel && (
          <MMButton
            type='button'
            variant='secondary'
            caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
            disabled={resendOtpFetching || verifyOtpFetching}
            onClick={internalCancel}
            className={cn('mt-1')}
            fullWidth
            data-test='button-cancel'
          />
        )}
      </div>

      <div className={cn(styles['otp-footer'], 'otp-footer', 'font-body-regular-small')}>
        <div className={cn(styles['otp-reset'], 'otp-reset')}>
          {isTotp ? null : attempts >= globalSettings.otpMaxAttempts ? (
            tr('FRONTEND.VERIFY_OTP.NO_MORE_ATTEMPTS', 'No more verification attempts.')
          ) : (
            <>
              {tr('FRONTEND.VERIFY_OTP.NOT_RECEIVED', "Haven't received it?")}
              <span
                className={cn({
                  [styles['otp-reset-disabled']]: !resendTimer.expired,
                  'otp-reset-disabled': !resendTimer.expired
                })}
                onClick={handleResend}
              >
                {resendTimer.expired
                  ? tr('FRONTEND.VERIFY_OTP.RESEND_CODE', 'Re-send code')
                  : resendTimer.remainingTime}
              </span>
            </>
          )}
        </div>

        {attempts >= globalSettings.otpMaxAttempts || resendOtpFetching || verifyOtpFetching ? null : (
          <div className={cn(styles['otp-reset-countdown'], 'otp-reset-countdown')}>{timer.formattedTime}</div>
        )}
      </div>
    </div>
  )
}

export default Otp
